import { Store, useStore } from '@tanstack/react-store'
import type { RequestError } from 'components/api/extractHttpError'
import { useCallback, useMemo } from 'react'

import { logEvents } from './logEvents'
import type {
  AskQuestionPayload,
  ChatResponse,
} from './modules/AskCopilot/useAskCopilot'

export type Feed = { uid: string } & (Summary | AskCopilot)

type Summary = {
  type: 'summary'
  data: {
    summary: string
  }
}

type AskCopilotPending = {
  type: 'ask-copilot'
  status: 'pending'
  data: {
    chat: ChatResponse
    originalPayload: AskQuestionPayload
  }
}

type AskCopilotCompleteCanceled = {
  type: 'ask-copilot'
  status: 'complete' | 'canceled'
  data: {
    chat: ChatResponse
  }
}

type AskCopilotError = {
  type: 'ask-copilot'
  status: 'error'
  data: {
    question: string
    error?: RequestError
    originalPayload: AskQuestionPayload
  }
}

type AskCopilot =
  | AskCopilotPending
  | AskCopilotCompleteCanceled
  | AskCopilotError

export const store = new Store<{
  sessionUid: string
  feed: Feed[]
}>({ feed: [], sessionUid: '' })

export const useMutateFeed = (sessionUid: string) => {
  const add = useCallback(
    (data: Summary | AskCopilot) => {
      // Generate UID outside setState so we can return it
      const generatedUid = crypto.randomUUID()

      store.setState((d) => {
        if (sessionUid !== d.sessionUid) {
          console.debug('[feed] resetting due to sessionUid mismatch', {
            prevSessionUid: d.sessionUid,
            nextSessionUid: sessionUid,
          })

          return { sessionUid, feed: [{ uid: generatedUid, ...data }] }
        }

        const newState = {
          sessionUid,
          feed: [...d.feed, { uid: generatedUid, ...data }],
        }

        logEvents({
          eventType: 'feed_add',
          state: newState,
        })

        return newState
      })

      return generatedUid
    },
    [sessionUid],
  )

  const update = useCallback(
    (uid: string, data: Summary | AskCopilot) => {
      store.setState((d) => {
        if (sessionUid !== d.sessionUid) {
          return { sessionUid, feed: [] }
        }

        const newState = {
          sessionUid,
          feed: d.feed.map((item) =>
            item.uid === uid ? { uid, ...data } : item,
          ),
        }

        logEvents({
          eventType: 'feed_update',
          state: newState,
        })

        return newState
      })
    },
    [sessionUid],
  )

  const remove = useCallback(
    (uid: string) => {
      store.setState((d) => {
        if (sessionUid !== d.sessionUid) {
          return { sessionUid, feed: [] }
        }
        const newState = {
          sessionUid,
          feed: d.feed.filter((i) => !(i.uid === uid)),
        }

        logEvents({
          eventType: 'feed_removed',
          state: newState,
        })

        return newState
      })
    },
    [sessionUid],
  )

  return useMemo(() => ({ add, update, remove }), [add, update, remove])
}

export const useFeed = (sessionUid: string) => {
  const data = useStore(store)
  if (sessionUid === data.sessionUid) return data.feed
  return []
}

export const clearFeed = () => {
  store.setState({ feed: [], sessionUid: '' })
}

export const hydrateFeed = (sessionUid: string, feed: Feed[]) => {
  store.setState({ sessionUid, feed })
}
